// <copyright file="quote-item-group-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { buttonsSaveCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { checkValidations } from '../../../../webmodule-common/other/ui/data-entry-screen-helpers';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { LitBaseModal } from '../../../../webmodule-common/other/ui/modal/modal-factory-lit';

/**
 * the editable attributes of a quote item group. extend this container (plus one field binding
 * and one form row in the dialog) to introduce new group attributes.
 */
export interface QuoteItemGroupDialogData {
  name: string;
  excludeFromPricing: boolean;

  // --- MRD delivery-batch metadata (Task 230006) ---------------------------------------------------
  // Authored here, persisted to the group attributes and the DB `QuoteItemBatch` row. All optional so
  // a plain (non-batch) group is unaffected. `deliveryDate` is a SERVER date string (local start-of-day
  // as UTC, the app-wide day-date shape) in and out — the type=date input formatting is the tracker's job.
  deliveryDate?: string | null;
  customerRef?: string | null;
  comment?: string | null;
}

/**
 * modal for creating a quote item group or editing its attributes.
 */
export class QuoteItemGroupDialog extends LitBaseModal {
  public modalResult = false;
  private data: QuoteItemGroupDialogData;
  private mode: 'create' | 'edit';
  private batchLinkingEnabled: boolean;
  private dataBinding: DataBinding;
  private dataTracker: DataTracker;
  private forms: FormInputAssistant;

  private readonlyMode: boolean;

  constructor(
    initial: QuoteItemGroupDialogData,
    mode: 'create' | 'edit',
    batchLinkingEnabled: boolean,
    readonlyMode = false
  ) {
    super();
    this.mode = mode;
    this.batchLinkingEnabled = batchLinkingEnabled;
    this.readonlyMode = readonlyMode;
    this.data = { ...initial };
    this.dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `quoteitemgroup-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(this.dataBinding);
    this.dataTracker.addObjectBinding(() => this.data, 'name', 'name', FieldType.string, false);
    this.dataTracker.addObjectBinding(
      () => this.data,
      'excludeFromPricing',
      'excludeFromPricing',
      FieldType.boolean,
      false
    );
    // MRD batch metadata (Task 230006). deliveryDate is a DAY date and is bound as one: FieldType.date is
    // the app-wide contract for these (see quote validUntilDate, purchase-order installationDate). The
    // tracker owns both conversions — server ISO -> local yyyy-MM-dd for the input, and local yyyy-MM-dd ->
    // local-start-of-day-as-UTC on the way back — so the value round-trips in the author's zone instead of
    // walking a day earlier on every save. Nullable: a blank delivery date is a real state (the batch
    // inherits the quote's date), not a missing one.
    this.dataTracker.addObjectBinding(() => this.data, 'deliveryDate', 'deliveryDate', FieldType.date, true);
    this.dataTracker.addObjectBinding(() => this.data, 'customerRef', 'customerRef', FieldType.string, false);
    this.dataTracker.addObjectBinding(() => this.data, 'comment', 'comment', FieldType.string, false);
    // forceReadonly disables every input this form renders, so a readonly quote can still open the group to
    // view its batch metadata (delivery date / customer ref / comment) without being able to change it.
    this.forms = new FormInputAssistant(this.dataTracker, this.readonlyMode);
  }

  /**
   * show the dialog and return the entered attributes, or null when cancelled / opened read-only.
   */
  public static async Execute(
    initial: QuoteItemGroupDialogData,
    mode: 'create' | 'edit',
    batchLinkingEnabled: boolean,
    readonlyMode = false
  ): Promise<QuoteItemGroupDialogData | null> {
    const dialog = new QuoteItemGroupDialog(initial, mode, batchLinkingEnabled, readonlyMode);
    await dialog.showModal();
    return dialog.modalResult ? dialog.data : null;
  }

  get isFooterVisible(): boolean {
    return true;
  }

  title(): string | TemplateResult {
    if (this.readonlyMode) return LangFranchiseeQuoteItemsView.viewGroup();
    return this.mode === 'create'
      ? LangFranchiseeQuoteItemsView.createGroup()
      : LangFranchiseeQuoteItemsView.editGroup();
  }

  bodyTemplate(): TemplateResult {
    const forms = this.forms;
    // The MRD fields below only mean anything once the group is mirrored as a V6 quote item batch. For a
    // supplier whose V6 does not support batches (Task 230006 gate) a group is a Dealer-side organisation
    // device only, so offering a delivery date / customer ref / comment would promise a round-trip that
    // will never happen. Any previously captured values stay in the record, just unshown.
    const batchFields = this.batchLinkingEnabled
      ? html`
          ${forms.date(
            'deliveryDate',
            LangFranchiseeQuoteItemsView.batchDeliveryDate(),
            'date',
            LangFranchiseeQuoteItemsView.batchDeliveryDateTooltip()
          )}
          ${forms.text1('customerRef', LangFranchiseeQuoteItemsView.batchCustomerRef())}
          ${forms.note('comment', LangFranchiseeQuoteItemsView.batchComment())}
        `
      : html``;
    return html`
      <form class="form-one-col">
        <div class="row">
          <div class="form-column">
            ${forms.text1('name', LangFranchiseeQuoteItemsView.groupName())}
            ${forms.switch('excludeFromPricing', LangFranchiseeQuoteItemsView.excludeFromPricing(), {
              toolTip: LangFranchiseeQuoteItemsView.excludeFromPricingTooltip()
            })}
            ${batchFields}
          </div>
        </div>
      </form>
    `;
  }

  footerTemplate(): TemplateResult | null | undefined {
    const buttons = buttonsSaveCancel();
    // Read-only view: no Save, just a single button to close.
    if (this.readonlyMode) {
      return html`
        <webmodule-button size="small" variant="primary" @click=${() => this.hideModal()}>
          ${buttons.cancel}
        </webmodule-button>
      `;
    }
    return this.createConfirmCancelButtons(buttons.ok, () => this.saveAndClose(), buttons.cancel);
  }

  private async saveAndClose() {
    this.dataTracker.applyChangeToValue();
    this.data.name = this.data.name?.trim() ?? '';
    if (isEmptyOrSpace(this.data.name)) {
      await checkValidations([LangFranchiseeQuoteItemsView.groupNameRequired()]);
      return;
    }
    this.modalResult = true;
    await this.hideModal();
  }
}
